import { TaskListFilters } from 'types/store/tasksList';
import { Deserializer } from 'jsonapi-serializer';
import { JsonApiResponse } from 'types/store/jsonApi';
import { AvailableAssigneesResponse } from 'api/availableAssignees';
import {
  AssignmentStatsType,
  AvailableAssignee,
  AvailableAssigneeWithAssignmentStats,
} from 'types/store/availableAssignees';
import { getInitials } from 'utils/functions';

export interface GetAvailableAssigneesUrlParams {
  projectId?: string;
  name?: string;
  field?: string;
  limit?: number;
  filters?: TaskListFilters;
  includeDisabled?: boolean;
  exportState?: {
    selectAll: boolean;
    excludedIds: string[];
    selectedIds: string[];
  };
  includeAssignmentStats?: boolean;
}

export const getAvailableAssigneesUrl: (
  params: GetAvailableAssigneesUrlParams
) => string = (params) => {
  const baseUrl = '/v3/available-assignees';
  const urlParams = new URLSearchParams();
  const {
    projectId,
    name,
    includeDisabled,
    exportState,
    filters,
    limit,
    field,
    includeAssignmentStats,
  } = params;
  if (field) {
    urlParams.append('field[assignee]', field);
  } else {
    urlParams.append('field[assignee]', 'name,email,status,avatarUrl,disabled');
  }

  const { program, excludeProjectStatuses, ...restFilters } = filters ?? {};
  if (projectId) {
    urlParams.append('filter[projects][ids][]', projectId);
  }

  if (program) {
    program.forEach((p) => {
      urlParams.append('filter[projects][programs][]', p);
    });
  }

  if (excludeProjectStatuses) {
    excludeProjectStatuses.forEach((status) => {
      urlParams.append('filter[projects][excludeProjectStatuses][]', status);
    });
  }

  if (exportState) {
    if (exportState.selectAll) {
      urlParams.append(
        'filter[tasks][selectAll]',
        exportState.selectAll.toString()
      );
    }
    if (exportState.excludedIds && exportState.excludedIds.length > 0) {
      exportState.excludedIds.forEach((id) => {
        urlParams.append('filter[tasks][excludedIds][]', id);
      });
    }
    if (exportState.selectedIds && exportState.selectedIds.length > 0) {
      exportState.selectedIds.forEach((id) => {
        urlParams.append('filter[tasks][selectedIds][]', id);
      });
    }
  }

  if (restFilters) {
    Object.entries(restFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(`filter[tasks][${key}][]`, v);
        });
      } else {
        if (value) {
          urlParams.append(`filter[tasks][${key}]`, value.toString());
        }
      }
    });
  }

  if (name) {
    urlParams.append('filter[name]', name);
  }

  urlParams.append(
    'filter[includeDisabled]',
    includeDisabled?.toString() ?? 'true'
  );

  if (includeAssignmentStats) {
    urlParams.append('include', 'assignmentStats');
  }

  if (limit) {
    urlParams.append('page[limit]', limit.toString());
  } else {
    urlParams.append('page[limit]', '100');
  }

  return `${baseUrl}?${urlParams.toString()}`;
};

export const deserializeAvailableAssignees: (
  response: JsonApiResponse<AvailableAssigneesResponse>
) => Promise<{
  assignees: AvailableAssigneeWithAssignmentStats[];
  links: Record<string, string>;
  meta: Record<string, string>;
}> = async (response) => {
  const deserializer = new Deserializer({
    keyForAttribute: 'camelCase',
    transform: (record: AvailableAssigneesResponse) => {
      return {
        assignees: record.assignees.map((assignee) => ({
          id: assignee.id,
          ...assignee.attributes,
          assignmentStats: assignee.meta,
        })),
        links: response.links,
        meta: response.meta,
      };
    },
  });
  return await deserializer.deserialize(response);
};

export const getAssigneeCheckedStatusFromAssignmentStats: (
  assignmentStats: AssignmentStatsType
) => boolean | 'mixed' = (assignmentStats) => {
  switch (assignmentStats) {
    case AssignmentStatsType.ALL:
      return true;
    case AssignmentStatsType.SOME:
      return 'mixed';
    default:
      return false;
  }
};

export const getAssigneeInitials: (assignee: AvailableAssignee) => string = (
  assignee
) => {
  return getInitials({
    firstName: assignee.name.split(' ')[0] || '',
    lastName: assignee.name.split(' ')[1] || '',
  });
};
